import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
} from '@mui/material';
import {
  FileText,
  Shield,
  Users,
  Gavel,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Globe,
  Mail,
} from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Decentralized Identity Verification Platform Agreement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Last Updated: January 2025
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            By using DecentralizedKYC, you agree to these Terms of Service. Please read them carefully as they contain important information about your rights and obligations.
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileText size={28} />
            1. Service Overview
          </Typography>
          <Typography variant="body1" paragraph>
            DecentralizedKYC is a blockchain-based identity verification platform that enables users to create, manage, and share their identity credentials through NFT technology. Our platform provides:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle size={20} /></ListItemIcon>
              <ListItemText 
                primary="Identity NFT Minting" 
                secondary="Create a unique, non-transferable identity NFT that represents your verified identity on the blockchain."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle size={20} /></ListItemIcon>
              <ListItemText 
                primary="Multi-Template KYC Verification" 
                secondary="Complete various levels of identity verification including personal, professional, and financial credentials."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle size={20} /></ListItemIcon>
              <ListItemText 
                primary="Decentralized Data Storage" 
                secondary="Your identity data is stored securely using IPFS and blockchain technology, ensuring privacy and user control."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle size={20} /></ListItemIcon>
              <ListItemText 
                primary="Cross-Platform Integration" 
                secondary="Use your verified identity across multiple platforms and services without repeated verification processes."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={24} />
            2. User Eligibility and Account Creation
          </Typography>
          <Typography variant="body1" paragraph>
            To use DecentralizedKYC, you must:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Age Requirement" 
                secondary="Be at least 18 years old or the age of majority in your jurisdiction."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Legal Capacity" 
                secondary="Have the legal capacity to enter into binding agreements."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Wallet Ownership" 
                secondary="Own and control a compatible cryptocurrency wallet (e.g., MetaMask, WalletConnect)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Accurate Information" 
                secondary="Provide truthful, accurate, and complete information during the verification process."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="One Identity Per Person" 
                secondary="Each individual may create only one identity NFT. Multiple accounts or fraudulent identities are prohibited."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield size={24} />
            3. User Rights and Responsibilities
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Your Rights:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Data Ownership" 
                secondary="You retain full ownership and control of your identity data and NFT."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Access Control" 
                secondary="You have the right to grant or revoke access to your identity data at any time."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Portability" 
                secondary="Your identity NFT is portable and can be used across compatible platforms."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Verification Status" 
                secondary="You have the right to know the status of your verification and any reasons for rejection."
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Your Responsibilities:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Accurate Information" 
                secondary="Provide truthful and accurate information during all verification processes."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Wallet Security" 
                secondary="Maintain the security of your wallet and private keys. We cannot recover lost access."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Compliance" 
                secondary="Comply with all applicable laws and regulations in your jurisdiction."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Updates" 
                secondary="Keep your identity information current and notify us of any material changes."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <XCircle size={24} />
            4. Prohibited Activities
          </Typography>
          <Typography variant="body1" paragraph>
            The following activities are strictly prohibited:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Identity Fraud" 
                secondary="Creating false identities, impersonating others, or providing fraudulent documentation."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Multiple Accounts" 
                secondary="Creating multiple identity NFTs or attempting to circumvent the one-person-one-identity rule."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="System Abuse" 
                secondary="Attempting to hack, exploit, or abuse our platform, smart contracts, or infrastructure."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Illegal Activities" 
                secondary="Using our platform for money laundering, terrorism financing, or other illegal activities."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Misuse" 
                secondary="Attempting to access, collect, or misuse other users' identity data without authorization."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Gavel size={24} />
            5. Verification Process and Standards
          </Typography>
          <Typography variant="body1" paragraph>
            Our verification process is designed to ensure accuracy and compliance:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Review Process" 
                secondary="All KYC submissions are reviewed by authorized personnel with appropriate clearance levels."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Verification Standards" 
                secondary="We follow industry best practices and regulatory requirements for identity verification."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Rejection Rights" 
                secondary="We reserve the right to reject any verification request that doesn't meet our standards or raises compliance concerns."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Re-verification" 
                secondary="We may require periodic re-verification to maintain the accuracy and currency of your identity data."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            6. Platform Fees and Payments
          </Typography>
          <Typography variant="body1" paragraph>
            DecentralizedKYC operates on the following fee structure:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Identity NFT Minting" 
                secondary="Free for all users (you only pay blockchain gas fees)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Basic KYC Verification" 
                secondary="Free for standard personal identity verification."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Premium Services" 
                secondary="Advanced KYC templates and expedited processing may incur fees as disclosed during the process."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Third-Party Integration" 
                secondary="Fees may apply for certain third-party platform integrations as disclosed by those platforms."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertTriangle size={24} />
            7. Disclaimers and Limitations
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> DecentralizedKYC is provided "as is" without warranties of any kind.
            </Typography>
          </Alert>
          <List>
            <ListItem>
              <ListItemText 
                primary="Blockchain Risks" 
                secondary="Blockchain transactions are irreversible. We cannot reverse transactions or recover lost private keys."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Technical Risks" 
                secondary="Smart contracts and blockchain technology carry inherent risks including bugs, network congestion, and protocol changes."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Regulatory Changes" 
                secondary="Changes in laws or regulations may affect the availability or functionality of our services."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Third-Party Services" 
                secondary="We are not responsible for the actions or policies of third-party platforms that integrate with our service."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            8. Service Modifications and Termination
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Service Updates" 
                secondary="Modify, update, or discontinue any aspect of our service with reasonable notice."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Account Suspension" 
                secondary="Suspend or terminate accounts that violate these terms or engage in prohibited activities."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Emergency Actions" 
                secondary="Take immediate action to protect the platform and users in case of security threats or legal requirements."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Globe size={24} />
            9. Governing Law and Dispute Resolution
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms of Service are governed by the laws of [Jurisdiction]. Any disputes arising from these terms or your use of DecentralizedKYC will be resolved through:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Good Faith Negotiation" 
                secondary="We encourage users to contact us first to resolve disputes amicably."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Arbitration" 
                secondary="Unresolved disputes may be subject to binding arbitration as specified in our dispute resolution policy."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Class Action Waiver" 
                secondary="You agree to resolve disputes individually and waive the right to participate in class action lawsuits."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            10. Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We may update these Terms of Service from time to time. Significant changes will be communicated through:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Platform Notifications" 
                secondary="In-app notifications and email alerts to registered users."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Website Updates" 
                secondary="Updated terms posted on our official website with revision dates."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Community Channels" 
                secondary="Announcements through our official social media and community channels."
              />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph>
            Continued use of DecentralizedKYC after changes constitutes acceptance of the updated terms.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Mail size={24} />
            Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            For questions about these Terms of Service or our platform:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: legal@dekyc.org<br />
            Website: https://dekyc.org<br />
            Community: https://discord.gg/dekyc<br />
            Documentation: https://docs.dekyc.org
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2">
            <strong>Effective Date:</strong> These terms become effective immediately upon your first use of DecentralizedKYC and remain in effect until terminated in accordance with these terms.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default TermsOfServicePage;